import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add any auth headers if needed
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.data)
      return Promise.reject(error.response.data)
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network Error:', error.message)
      return Promise.reject({
        success: false,
        message: 'Network error. Please check your connection and try again.'
      })
    } else {
      // Something else happened
      console.error('Request Error:', error.message)
      return Promise.reject({
        success: false,
        message: 'An unexpected error occurred.'
      })
    }
  }
)

// API methods
export const registrationAPI = {
  // Save registration data
  async saveRegistration(registrationData) {
    try {
      const response = await apiClient.post('/api/registrations', registrationData)
      return response
    } catch (error) {
      throw error
    }
  },

  // Get registration by email
  async getRegistrationByEmail(email) {
    try {
      const response = await apiClient.get(`/api/registrations/${encodeURIComponent(email)}`)
      return response
    } catch (error) {
      if (error.status === 404) {
        return null // Registration not found
      }
      throw error
    }
  },

  // Save draft when checkbox is clicked
  async saveDraft(draftData) {
    try {
      const response = await apiClient.post('/api/save-draft', draftData)
      return response
    } catch (error) {
      throw error
    }
  },

  // Upload passport image + payment receipt (Step 3 — Document Upload)
  // Expects a FormData instance with the fields:
  //   - passport  (File)  required
  //   - receipt   (File)  required
  //   - email     (string) required — used to locate the existing registration row
  //   - registrationId (string) optional — present once /api/registrations has returned an ID
  //   - paymentReference (string) optional — transaction ID / bank slip ref
  async uploadDocuments(formData) {
    try {
      const response = await apiClient.post('/api/registrations/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // larger window for file uploads
      })
      return response
    } catch (error) {
      throw error
    }
  },

  // Complete payment for registration
  async completePayment(registrationData) {
    try {
      // Use email to identify the registration for completion
      const email = registrationData.billingDetails?.email || registrationData.email
      if (!email) {
        throw new Error('Email is required to complete payment')
      }

      const response = await apiClient.post(`/api/registrations/complete`, {
        email: email,
        paymentData: registrationData.paymentData || {},
        billingDetails: registrationData.billingDetails,
        total: registrationData.total,
        currency: registrationData.currency
      })
      return response
    } catch (error) {
      throw error
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await apiClient.get('/health')
      return response
    } catch (error) {
      throw error
    }
  }
}

export default {
  registrationAPI
}